import React from 'react';
import { Helmet } from 'react-helmet';
import { Box } from 'localmed-core';
import styled from '@emotion/styled';
import { css } from '@emotion/core';

import Profile from './Components/Profile';
import ProviderPageTab from './Components/ProviderPageTab';
import ProviderPageQuery from './ProviderPageQuery';

import LoadingPage from '../LoadingPage';
import isForbidden from '../../../shared/utils/isForbidden';
import Unauthorized from '../../../shared/components/Unauthorized';
import MainLayout from '../../components/MainLayout/MainLayout';
import NotFoundPage from '../NotFoundPage';
import { buildProviderSchedulingUrl } from '../../../shared/utils/urls';
import ProviderPageCalendar from './Components/ProviderPageCalendar';
import getSchedulingParams from '../../../shared/utils/getSchedulingParams';
import { PATIENT_STATUS } from '../../../shared/constants';
import { createSchedulingSessionStateLandingPage } from '../../../shared/components/SchedulingSessionState';
import TrackViewedProviderEvent from '../../../shared/components/TrackViewedProviderEvent';
import { ProviderSchemaSerializer } from './utils';

const Container = styled(Box)(
  ({ theme }) => css`
    max-width: ${theme.breakpoints.xl};
    margin: 0 auto;

    form {
      border: 1px solid ${theme.colors.legacyBorder};
    }
  `
);

function ProviderPage(props) {
  const { id } = props.match.params;
  const { location } = props.history;

  return (
    <ProviderPageQuery providerId={id}>
      {({ provider, loading, error }) => {
        if (loading) return <LoadingPage />;
        if (isForbidden(error)) {
          return (
            <Unauthorized>
              <LoadingPage />
            </Unauthorized>
          );
        }
        if (error) return <MainLayout error={error} />;

        if (!provider || provider.archivedAt || !provider.localmedDirectoryEnabled)
          return <NotFoundPage />;

        const { office, practice, defaultReasonForVisit } = provider;
        const providers = provider.allProvidersForProfile.nodes;

        const reasonsForVisitVisibleOnDirectory =
          office.reasonsForVisit?.nodes.filter((r) => r.visibleOnDirectory) || [];

        const schedulingParams = getSchedulingParams({
          history: props.history,
          reasonsForVisit: reasonsForVisitVisibleOnDirectory,
          defaultReasonForVisit,
          autoSelectReason: true,
        });
        const { reasonForVisit, reasonsForVisit, patientStatus } = schedulingParams.values;

        const patientStatusItems = [
          { id: PATIENT_STATUS.NEW, display: 'New Patient' },
          { id: PATIENT_STATUS.RETURNING, display: 'Returning Patient' },
        ];
        const selectedPatientStatus =
          patientStatusItems.find((p) => p.id === patientStatus) || patientStatusItems[0];

        return (
          <MainLayout>
            <Helmet>
              <title>
                {`${provider.display} | ${office.googlePlaceName || practice.name} | ${
                  office.cityDetails.name
                } | Book Online with LocalMed`}
              </title>
              <meta
                name="description"
                content={`Schedule online with ${provider.display} in ${office.cityDetails.name}, ${office.stateDetails.name} for FREE. LocalMed shows ${provider.display}’s open appointments online for you to book.`}
              />
              <meta name="robots" content="index" />
              <link
                rel="canonical"
                href={`${global.CLIENT_ENV.BASE_URL}${buildProviderSchedulingUrl(provider.slug)}`}
              />
              <script type="application/ld+json">
                {JSON.stringify(ProviderSchemaSerializer(provider, global.CLIENT_ENV.LOCALE))}
              </script>
            </Helmet>
            <Box>
              {!loading && <TrackViewedProviderEvent provider={provider} id={id} />}
              <Profile provider={provider} activeOffice={office} />
              <Box paddingX={{ _: 'sm', lg: 'md' }} paddingY="md">
                <Container display={{ _: 'block', lg: 'flex' }} alignItems="baseline">
                  <ProviderPageCalendar
                    width={{ _: 1, lg: 2 / 3 }}
                    order={{ _: 1, lg: 2 }}
                    provider={provider}
                    reasonsForVisit={reasonsForVisit}
                    onlineSchedulingEnabled={provider.onlineSchedulingEnabled}
                    office={office}
                    selectedPatientStatus={selectedPatientStatus}
                    reasonForVisit={reasonForVisit}
                    schedulingParams={schedulingParams}
                  />
                  <ProviderPageTab
                    width={{ _: 1, lg: 1 / 3 }}
                    order={{ _: 2, lg: 1 }}
                    marginRight={{ _: 0, lg: 'md' }}
                    marginTop={{ _: 'md', lg: 0 }}
                    provider={provider}
                    activeOfficeId={office.id}
                    setActiveOfficeId={(officeId) => {
                      const currentProvider = providers.find((p) => p.office.id === officeId);
                      if (currentProvider)
                        props.history.push(
                          buildProviderSchedulingUrl(currentProvider.id, { location })
                        );
                    }}
                  />
                </Container>
              </Box>
            </Box>
          </MainLayout>
        );
      }}
    </ProviderPageQuery>
  );
}

export default createSchedulingSessionStateLandingPage(ProviderPage);
